/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021-2025, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

/**
 * @module bulb-image-carousel/bulbimagecarouselui
 */

import { Plugin, ButtonView } from 'ckeditor5';
import type { Locale, Command } from 'ckeditor5';
import bulbImageCarouselIcon from '../../assets/plugin_icons/image-carousel-button.svg';
import { assetInsertLocation, cancelBusyIndicator } from '../utils';

/**
 * BulbImageCarouselUI plugin. Handles our bulb image carousel UI.
 *
 * Creates and handles the componentFactory to add to the cke toolbar, business logic, and button execution / state.
 */
export default class BulbImageCarouselUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageCarouselUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;

    editor.ui.componentFactory.add('bulbInsertImageCarousel', (locale: Locale): ButtonView => {
      // The state of the button will be bound to the widget command.
      const command: Command = editor.commands.get('insertImageCarousel');

      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('editor_image_carousel_convert_title'),
        icon: bulbImageCarouselIcon,
        tooltip: true,
        withText: true,
      });

      buttonView.on('execute', () => {
        editor.model.change(() => {
          this.openAssetLibrarySelectionModal();
        });
      });

      // Initializing imageCarouselUploadCompleteEvent.
      CKEBulbData.service.ckeStateService.initializeImageCarouselUploadEvent();

      // Listen for the event that is going to be fired from our client that the upload was successful.
      CKEBulbData.service.ckeStateService.imageCarouselUploadCompleteEvent.subscribe((event: CustomEvent) => {
        CKEBulbData.service.dialog.showOrientationDialog(
          false,
          event.detail.contentBlock.carouselImages[0].fullImageAsset.url,
          event.detail.contentBlock,
          editor,
          event.detail.orientation,
          0,
          event.detail.noOfAssets,
        );
        const editingView = editor.editing.view;
        editingView.focus();
      });

      CKEBulbData.service.ckeStateService.imageCarouselCropUploadCompleteEvent.subscribe((event: CustomEvent) => {
        if (event.type === 'imageCarouselUploadComplete') {
          if (!CKEBulbData.service.ckeStateService.getDesktopUploadSelectionMode()) {
            assetInsertLocation(this.editor);
          }
          CKEBulbData.service.ckeStateService.setDesktopUploadSelectionMode(false);
          editor.execute('insertImageCarousel', event.detail.contentBlock);
          cancelBusyIndicator(editor);
        }
      });

      // Bind the state of the button to the command.
      buttonView.bind('isOn', 'isEnabled').to(command as any, 'value', 'isEnabled');

      return buttonView;
    });
  }

  /**
   * @inheritDoc
   */
  public destroy(): void {
    // Complete the observable.
    (this.editor as any).bulb?.service.ckeStateService.imageCarouselUploadCompleteEvent.complete();
  }

  /**
   * Method reference on our Carousel button to execute an event to open up our assetlibrary selection modal.
   *
   * @private
   */
  private openAssetLibrarySelectionModal() {
    CKEBulbData.service.dialog.showOrientationDialog(true);
  }
}
